import React from 'react';
import { ROWS, ROW_COLORS, rightmostMarkedIndex } from '../../shared/game.mjs';

const labels = { red: 'Rot', yellow: 'Gelb', green: 'Grün', blue: 'Blau' };

export function ScoreSheet({ player, lockedRows, availableMoves = [], recommendedMoves = [], onMove, interactive = false, compact = false, childGuidance = false }) {
  const available = new Set(availableMoves.map((move) => `${move.color}:${move.value}`));
  const recommended = new Set(recommendedMoves.map((move) => `${move.color}:${move.value}`));
  const board = player.board;

  return (
    <section className={`score-sheet ${compact ? 'score-sheet--compact' : ''}`} aria-label={`Wertungsblatt von ${player.name}`}>
      <header className="score-sheet__header">
        <div>
          <strong>{player.name}</strong>
          {player.isBot && <span className="tag">Bot</span>}
          {!player.connected && <span className="tag tag--warning">offline</span>}
        </div>
        <span className="score-total">{player.score.total} P.</span>
      </header>

      <div className="rows">
        {ROW_COLORS.map((color) => {
          const row = board.rows[color];
          const lastIndex = rightmostMarkedIndex(board, color);
          const globallyLocked = Boolean(lockedRows[color]);
          const closingValue = ROWS[color][ROWS[color].length - 1];
          const canClose = interactive && available.has(`${color}:${closingValue}`);
          return (
            <div className={`score-row score-row--${color} ${globallyLocked ? 'is-locked' : ''}`} key={color}>
              <span className="score-row__label">{labels[color]}</span>
              <div className="score-row__numbers">
                {ROWS[color].map((value, index) => {
                  const marked = row.marked.includes(value);
                  const skipped = !marked && index <= lastIndex;
                  const enabled = interactive && available.has(`${color}:${value}`);
                  const suggested = enabled && recommended.has(`${color}:${value}`);
                  const childAlternative = childGuidance && enabled && !suggested;
                  const final = index === ROWS[color].length - 1;
                  return (
                    <button
                      type="button"
                      className={`number-cell ${marked ? 'is-marked' : ''} ${skipped ? 'is-skipped' : ''} ${enabled ? 'is-available' : ''} ${suggested ? 'is-recommended' : ''} ${childAlternative ? 'is-child-alternative' : ''} ${final ? 'is-final' : ''}`}
                      key={value}
                      disabled={!enabled}
                      onClick={() => enabled && onMove?.({ color, value })}
                      aria-label={`${labels[color]} ${value}${marked ? ', angekreuzt' : ''}${enabled ? ', auswählbar' : ''}${suggested ? ', Tipp' : ''}`}
                    >
                      {suggested && <span className="recommendation-label" aria-hidden="true">👆</span>}
                      {marked ? <span aria-hidden="true">×</span> : value}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`lock-cell ${row.lock ? 'is-owned' : ''} ${globallyLocked ? 'is-closed' : ''} ${canClose ? 'is-available' : ''}`}
                  title={globallyLocked ? 'Reihe geschlossen' : canClose ? `Reihe mit ${closingValue} schließen` : 'Reihenschluss'}
                  aria-label={row.lock ? 'Eigenes Reihenschloss' : globallyLocked ? 'Reihe geschlossen' : canClose ? `${labels[color]} mit ${closingValue} schließen` : 'Reihenschloss'}
                  disabled={!canClose}
                  onClick={() => canClose && onMove?.({ color, value: closingValue })}
                >
                  🔒
                </button>
              </div>
              <span className="row-score">{player.score.rows[color]}</span>
            </div>
          );
        })}
      </div>

      <footer className="score-sheet__footer">
        <div className="penalties" aria-label={`${board.penalties} Fehlwürfe`}>
          <span>Fehlwürfe</span>
          {[0, 1, 2, 3].map((index) => <i key={index} className={index < board.penalties ? 'is-filled' : ''}>{index < board.penalties ? '×' : '−5'}</i>)}
        </div>
        <div className="score-formula">
          <span>{player.score.subtotal}</span><b>−</b><span>{player.score.penalty}</span><b>=</b><strong>{player.score.total}</strong>
        </div>
      </footer>
    </section>
  );
}
